import {parseCookies} from 'nookies';
import {BASEURL, TEKIE_INTERNAL_ACCESS} from './constant';
import {
    SCRATCH_UPLOAD_STATUS,
    setScratchUploadStatus
} from './scratch-upload-status';

const DEFAULT_UPLOAD_CHUNK_SIZE_BYTES = 256 * 1024;
const LOCAL_UPLOAD_CHUNK_SIZE_BYTES = 10 * 1024;
const UPLOAD_CHUNK_SIZE_BYTES = process.env.NODE_ENV === 'local' ?
    LOCAL_UPLOAD_CHUNK_SIZE_BYTES :
    DEFAULT_UPLOAD_CHUNK_SIZE_BYTES;

const readBlobAsBase64 = blobPart =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onloadend = () => {
            const encoded = reader.result.replace(/^data:.+;base64,/, '');
            resolve(encoded);
        };
        reader.readAsDataURL(blobPart);
    });

const getBlobPartRange = (partNumber, totalBytes) => {
    const partStart = partNumber * UPLOAD_CHUNK_SIZE_BYTES;
    const partEnd = Math.min(
        partStart + UPLOAD_CHUNK_SIZE_BYTES,
        totalBytes
    );
    return {partStart, partEnd};
};

const parseUserIds = cookieUserIds => {
    try {
        const parsed = JSON.parse(cookieUserIds);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
};

const getUploadConfig = () => {
    let apiBaseUrl = `https://${BASEURL.tekieStage}/api/upload-scratch`;
    let authorization = TEKIE_INTERNAL_ACCESS.stage;
    if (process.env.NODE_ENV === 'prod' || process.env.NODE_ENV === 'production') {
        apiBaseUrl = `https://${BASEURL.tekieProd}/api/upload-scratch`;
        authorization = TEKIE_INTERNAL_ACCESS.prod;
    } else if (process.env.NODE_ENV === 'local') {
        apiBaseUrl = `http://${BASEURL.tekieLocal}/api/upload-scratch`;
        authorization = TEKIE_INTERNAL_ACCESS.stage;
    }
    return {apiBaseUrl, authorization};
};

const postUploadJson = ({
    apiUrl,
    authorization,
    body,
    expectedStatus
}) =>
    fetch(apiUrl, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: Object.assign(
            {'Content-Type': 'application/json'},
            authorization ? {Authorization: authorization} : {}
        )
    })
        .then(response =>
            response
                .json()
                .catch(() => ({}))
                .then(responseBody => ({response, responseBody}))
        )
        .then(({response, responseBody}) => {
            if (!response.ok || response.status !== expectedStatus) {
                const serverError = responseBody.error || responseBody.message;
                throw new Error(
                    serverError || `Upload request failed with status ${response.status}`
                );
            }
            return responseBody;
        });

const uploadScratchInChunks = ({blob, cookies}) => {
    const userIds = parseUserIds(cookies.buddyUserIds);
    const projectId =
        cookies.blockBasedPracticeId !== 'undefined' &&
        cookies.blockBasedPracticeId ?
            cookies.blockBasedPracticeId :
            cookies.codeId;
    const isPractice = !(
        cookies.blockBasedPracticeId !== 'undefined' &&
        cookies.blockBasedPracticeId
    );

    if (!userIds.length || !projectId || projectId === 'undefined') {
        setScratchUploadStatus(SCRATCH_UPLOAD_STATUS.SAVED);
        return {ok: false, error: 'Missing upload metadata in cookies'};
    }

    const {apiBaseUrl, authorization} = getUploadConfig();
    const totalBytes = blob.size;
    const totalParts = Math.max(
        1,
        Math.ceil(totalBytes / UPLOAD_CHUNK_SIZE_BYTES)
    );

    setScratchUploadStatus(SCRATCH_UPLOAD_STATUS.SAVING);

    let uploadId = null;
    const firstPartRange = getBlobPartRange(0, totalBytes);
    const firstBlobPart = blob.slice(firstPartRange.partStart, firstPartRange.partEnd);

    return readBlobAsBase64(firstBlobPart)
        .then(firstBlobBase64 => postUploadJson({
            apiUrl: `${apiBaseUrl}/init`,
            authorization,
            body: {
                userIds,
                isPractice,
                projectId,
                totalParts,
                totalBytes,
                partNumber: 0,
                blob: firstBlobBase64
            },
            expectedStatus: 200
        }))
        .then(initResponse => {
            uploadId = initResponse.uploadId;
            if (!uploadId) {
                throw new Error('uploadId missing from init response');
            }

            const middlePartCount = Math.max(0, totalParts - 2);
            const middlePartNumbers = Array.from(
                {length: middlePartCount},
                (_, index) => index + 1
            );
            return middlePartNumbers.reduce(
                (partUploadChain, partNumber) =>
                    partUploadChain.then(() => {
                        const partRange = getBlobPartRange(partNumber, totalBytes);
                        const partStart = partRange.partStart;
                        const partEnd = partRange.partEnd;
                        const blobPart = blob.slice(partStart, partEnd);
                        return readBlobAsBase64(blobPart)
                            .then(blobBase64 => postUploadJson({
                                apiUrl: `${apiBaseUrl}/part`,
                                authorization,
                                body: {
                                    uploadId,
                                    partNumber,
                                    blob: blobBase64
                                },
                                expectedStatus: 202
                            }));
                    }),
                Promise.resolve()
            );
        })
        .then(() => {
            if (totalParts <= 1) {
                return postUploadJson({
                    apiUrl: `${apiBaseUrl}/complete`,
                    authorization,
                    body: {uploadId},
                    expectedStatus: 200
                });
            }
            const finalPartNumber = totalParts - 1;
            const finalPartRange = getBlobPartRange(finalPartNumber, totalBytes);
            const finalBlobPart = blob.slice(finalPartRange.partStart, finalPartRange.partEnd);
            return readBlobAsBase64(finalBlobPart)
                .then(finalBlobBase64 => postUploadJson({
                    apiUrl: `${apiBaseUrl}/complete`,
                    authorization,
                    body: {
                        uploadId,
                        partNumber: finalPartNumber,
                        blob: finalBlobBase64
                    },
                    expectedStatus: 200
                }));
        })
        .then(() => {
            setScratchUploadStatus(SCRATCH_UPLOAD_STATUS.SAVED);
            return {ok: true};
        })
        .catch(error => {
            setScratchUploadStatus(SCRATCH_UPLOAD_STATUS.SAVED);
            return {ok: false, error};
        });
};

export default (filename, blob) => {
    const cookies = parseCookies();
    const uploadPromise = filename === 'autoSave' ?
        uploadScratchInChunks({blob, cookies}) :
        Promise.resolve({ok: true, skipped: true});

    if (filename !== 'autoSave') {
        // Use special ms version if available to get it working on Edge.
        if (typeof navigator !== 'undefined' && navigator.msSaveOrOpenBlob) {
            navigator.msSaveOrOpenBlob(blob, filename);
            return uploadPromise;
        }

        if (typeof HTMLAnchorElement !== 'undefined' && 'download' in HTMLAnchorElement.prototype) {
            const downloadLink = document.createElement('a');
            document.body.appendChild(downloadLink);
            const url = window.URL.createObjectURL(blob);

            downloadLink.href = url;
            downloadLink.download = filename;
            downloadLink.type = blob.type;
            downloadLink.click();

            window.setTimeout(() => {
                document.body.removeChild(downloadLink);
            }, 1000);
        } else {
            // iOS 12 Safari, open a new page and set href to data-uri
            let popup = window.open('', '_blank');
            const reader = new FileReader();
            reader.onloadend = function () {
                popup.location.href = reader.result;
                popup = null;
            };
            reader.readAsDataURL(blob);
        }
    }

    return uploadPromise;
};
